import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Snackbar,
  Alert,
  AlertTitle,
  IconButton,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Tooltip,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import api from '../utils/api';
import { printScreenplayAnalysis } from '../utils/printScreenplayAnalysis';
import {
  ArrowBack as BackIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  BarChart as ChartIcon,
  Assignment as ReportIcon,
  Movie as MovieIcon,
  Person as PersonIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Groups as GroupsIcon,
  AttachMoney as MoneyIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * ScreenplayAnalysisViewer Component
 * 
 * Displays the results of a screenplay analysis, including film coverage report,
 * comparable films, and analytics.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.analysis - The screenplay analysis data
 * @param {string} props.projectId - ID of the current project
 * @param {string} props.projectName - Name of the current project
 * @param {Function} props.retryAnalysis - Callback function to retry analysis
 */
const extractActorNames = (film = {}) => {
  const names = [];
  const pushName = (entry) => {
    if (!entry) return;
    const trimmed = entry.trim();
    if (trimmed && !names.includes(trimmed)) {
      names.push(trimmed);
    }
  };

  if (film.talentDetails) {
    ['lead', 'secondLead', 'supporting'].forEach((role) => {
      const roleEntry = film.talentDetails[role];
      if (roleEntry?.name) {
        pushName(roleEntry.name);
      }
    });
  }

  if (!names.length && typeof film.talent === 'string') {
    const starsMatch = film.talent.match(/Stars?\s*[:\-]\s*([^;]+)/i);
    const actorSegment = starsMatch ? starsMatch[1] : film.talent;
    actorSegment
      .split(/,|&| and |\/|\|/i)
      .map((part) => part.replace(/^\s*as\s+.+$/i, '').trim())
      .filter(Boolean)
      .forEach(pushName);
  }

  return names;
};

const ScreenplayAnalysisViewer = ({ analysis, projectId, projectName, retryAnalysis, onBack }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisState, setAnalysis] = useState(analysis);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [includeCoverage, setIncludeCoverage] = useState(true);
  const [includeComparables, setIncludeComparables] = useState(true);

  // Helper function to convert marketability text to score (0-100)
  const getMarketabilityScore = (marketability) => {
    if (!marketability) return 0;
    
    switch(marketability.toLowerCase()) {
      case 'high': return 85;
      case 'medium': return 60;
      case 'low': return 30;
      default: return 0;
    }
  };

  // Initialize analysis state from props
  useEffect(() => {
    if (analysis) {
      setAnalysis(analysis);
    }
  }, [analysis]);

  // Status polling effect
  useEffect(() => {
    let pollingInterval = null;
    
    const fetchAnalysis = async () => {
      try {
        if (!analysisState || !analysisState._id) {
          return;
        }
        
        setLoading(true);

        const response = await api.get(`/screenplay-analysis/${analysisState._id}`, {
          params: { projectId: analysisState.projectId }
        });

        if (response && response.data) {
          // Check if status has changed
          const statusChanged = analysisState.status !== response.data.status;
          if (statusChanged) {
            // Show notification for status change
            let message = '';
            let severity = 'info';
            
            switch (response.data.status) {
              case 'completed':
                message = 'Analysis completed successfully!';
                severity = 'success';
                break;
              case 'completed_partial':
                message = 'Analysis completed with some limitations.';
                severity = 'warning';
                break;
              case 'processing':
                message = 'Analysis is now processing...';
                severity = 'info';
                break;
              case 'error':
                message = `Analysis failed: ${response.data.errorMessage || 'Unknown error'}`;
                severity = 'error';
                break;
              default:
                message = `Analysis status changed to: ${response.data.status}`;
            }
            
            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
            setOpenSnackbar(true);
            
            // Force UI update by creating a new object reference
            setAnalysis({...response.data});
          } else {
            // Even if status hasn't changed, update the analysis state to get any other changes
            // Use a new object reference to ensure React detects the change
            setAnalysis({...response.data});
          }
          
          setLoading(false);
        } else {
          throw new Error('No data returned from server');
        }
      } catch (error) {
        console.error('Error fetching analysis:', error);
        setError('Failed to load analysis. Please try again later.');
        setLoading(false);
      }
    };

    // Check if analysis is already in a completed state
    const isCompleted = analysisState && (
      analysisState.status === 'completed' || 
      analysisState.status === 'completed_partial' || 
      analysisState.status === 'error'
    );

    // Start polling if we have an analysis with an ID and it's not in a completed state
    if (analysisState && analysisState._id) {
      if (!isCompleted) {
        // Initial fetch
        fetchAnalysis();
        
        // Set up polling only for non-completed analyses
        pollingInterval = setInterval(() => {
          fetchAnalysis();
        }, 2000); // Poll every 2 seconds for faster updates
      }
      // Completed analyses: data is already in props, no fetch needed
    }
    
    // Cleanup function to clear the interval when component unmounts or dependencies change
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [analysisState?._id, analysisState?.projectId]); // Only depend on ID and projectId — not status

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Handle print
  const handlePrint = () => {
    if (!analysisState) return;
    try {
      printScreenplayAnalysis(analysisState, {
        projectName,
        includeCoverage,
        includeComparables,
      });
      setPrintDialogOpen(false);
    } catch (err) {
      console.error('Failed to print screenplay analysis:', err);
      setSnackbarMessage('Failed to generate printable report.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  // Handle retry analysis
  const handleRetryAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the retryAnalysis prop from Manager if available, otherwise call API directly
      if (retryAnalysis && analysisState?._id) {
        retryAnalysis(analysisState._id);
        setSnackbarMessage('Analysis retry initiated successfully. This may take a few minutes to complete.');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } else {
        await api.post(`/screenplay-analysis/${analysisState._id}/retry`, {
          projectId: analysisState.projectId
        });

        // Fetch the updated analysis
        const fetchResponse = await api.get(`/screenplay-analysis/${analysisState._id}`, {
          params: { projectId: analysisState.projectId }
        });

        setAnalysis(fetchResponse.data);
        setSnackbarMessage('Analysis retry initiated successfully. This may take a few minutes to complete.');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error retrying analysis:', error);
      setError('Failed to retry analysis. Please try again later.');
      setLoading(false);
      setSnackbarMessage('Failed to retry analysis');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  // Render status message
  const renderStatusMessage = () => {
    if (!analysisState) return null;
    
    switch (analysisState.status) {
      case 'pending':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Analysis Pending</AlertTitle>
            Your screenplay analysis is in the queue and will be processed shortly. This page will automatically update when processing begins.
          </Alert>
        );
      case 'processing':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Analysis in Progress</AlertTitle>
            Your screenplay is currently being analyzed. This process typically takes 3-5 minutes. This page will automatically update when complete.
          </Alert>
        );
      case 'completed_partial':
        return (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Partial Analysis Completed</AlertTitle>
            Your screenplay analysis completed with some limitations. {analysisState.errorMessage || 'Some components could not be fully processed.'}
            <Box sx={{ mt: 1 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="warning" 
                onClick={handleRetryAnalysis}
                startIcon={<RefreshIcon />}
              >
                Retry Analysis
              </Button>
            </Box>
          </Alert>
        );
      case 'error':
        return (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Analysis Error</AlertTitle>
            {analysisState.errorMessage || 'An error occurred during analysis. Please try again.'}
            <Box sx={{ mt: 1 }}>
              <Button 
                size="small" 
                variant="outlined" 
                color="error" 
                onClick={handleRetryAnalysis}
                startIcon={<RefreshIcon />}
              >
                Retry Analysis
              </Button>
            </Box>
          </Alert>
        );
      case 'completed':
        return null; // No message needed for successful completion
      default:
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Status: {analysisState.status}</AlertTitle>
            {analysisState.errorMessage || 'Analysis status is being tracked.'}
          </Alert>
        );
    }
  };

  // Render status badge
  const renderStatusBadge = () => {
    if (!analysisState) return null;
    
    let color = 'default';
    let label = 'Unknown';
    let icon = null;
    
    switch (analysisState.status) {
      case 'pending':
        color = 'secondary';
        label = 'Pending';
        icon = <HourglassEmptyIcon fontSize="small" />;
        break;
      case 'processing':
        color = 'primary';
        label = 'Processing';
        icon = <CircularProgress size={16} thickness={5} />;
        break;
      case 'completed':
        color = 'success';
        label = 'Completed';
        icon = <CheckCircleIcon fontSize="small" />;
        break;
      case 'completed_partial':
        color = 'warning';
        label = 'Partial';
        icon = <WarningIcon fontSize="small" />;
        break;
      case 'error':
        color = 'error';
        label = 'Error';
        icon = <ErrorIcon fontSize="small" />;
        break;
      default:
        color = 'default';
        label = analysisState.status || 'Unknown';
    }
    
    return (
      <Chip
        icon={icon}
        label={label}
        color={color}
        size="small"
        sx={{ 
          fontWeight: 'bold',
          ml: 2,
          '& .MuiChip-icon': { 
            mr: 0.5,
            color: 'inherit'
          }
        }}
      />
    );
  };

  // Render film coverage report tab
  const renderFilmCoverageReport = () => {
    // Show placeholder while analysis is in progress
    if (analysisState.status === 'pending' || analysisState.status === 'processing') {
      return (
        <Box sx={{ p: isMobile ? 2 : 4, textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Analyzing screenplay...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The coverage report will appear here once analysis completes.
          </Typography>
        </Box>
      );
    }

    if (!analysisState.filmCoverageReport || 
        !analysisState.filmCoverageReport.title || 
        (!analysisState.filmCoverageReport.logline && !analysisState.filmCoverageReport.synopsis)) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="textSecondary">
            Film coverage report is not available
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            The analysis may still be processing or encountered an error.
          </Typography>
        </Box>
      );
    }
    
    const report = analysisState.filmCoverageReport;
    
    return (
      <Box sx={{ p: isMobile ? 1 : 3 }}>
        {/* Professional Header - Studio Coverage Style */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: isMobile ? 3 : 5, 
            mb: 4, 
            borderRadius: 0,
            background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.primary.main,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            borderBottom: theme.palette.mode === 'dark' ? `4px solid ${theme.palette.primary.main}` : `4px solid ${alpha(theme.palette.common.white, 0.3)}`
          }}
        >
          {/* Coverage Label */}
          <Typography variant="overline" sx={{ 
            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : alpha(theme.palette.common.white, 0.8),
            fontWeight: 700,
            letterSpacing: 3,
            fontSize: '0.75rem',
            mb: 1,
            display: 'block'
          }}>
            SCREENPLAY COVERAGE REPORT
          </Typography>
          
          <Typography variant="h3" sx={{ 
            fontWeight: 700, 
            mt: 2,
            mb: 1,
            fontSize: isMobile ? '1.75rem' : '2.75rem',
            letterSpacing: '-0.02em'
          }}>
            {projectName || report.title || 'Untitled Screenplay'}
          </Typography>
          
          {report.logline && (
            <Typography variant="h6" sx={{ 
              fontStyle: 'italic', 
              mb: 3,
              fontWeight: 400,
              color: 'rgba(255,255,255,0.85)',
              maxWidth: 800,
              lineHeight: 1.5
            }}>
              "{report.logline}"
            </Typography>
          )}
          
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 3 }} />
          
          {/* Quick Info Row */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                Genre
              </Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {report.genre || 'Unknown'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem' }}>
                Rating
              </Typography>
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {report.ratingEstimate || 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          {/* Tone & Setting - Full Width */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {report.tone && report.tone !== 'N/A' && report.tone !== 'Not specified' && (
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Tone
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
                  {report.tone}
                </Typography>
              </Box>
            )}
            {report.setting && report.setting !== 'Contemporary' && report.setting !== 'Not specified' && (
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Setting
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.5 }}>
                  {report.setting}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
        
        {/* Synopsis */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 2,
            fontSize: '0.7rem',
            mb: 1,
            display: 'block'
          }}>
            PLOT SUMMARY
          </Typography>
          <Paper elevation={0} sx={{ 
            p: 3, 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.5)
          }}>
            <Typography variant="body1" sx={{ 
              lineHeight: 1.8,
              fontSize: '1.05rem',
              color: theme.palette.text.primary
            }}>
              {report.synopsis || 'No synopsis available'}
            </Typography>
          </Paper>
        </Box>
        
        {/* Themes - if available */}
        {report.themes && report.themes !== 'Not specified' && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 600,
              letterSpacing: 2,
              fontSize: '0.7rem',
              mb: 1,
              display: 'block'
            }}>
              CENTRAL THEMES
            </Typography>
            <Paper elevation={0} sx={{ 
              p: 2, 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.secondary.main, 0.03)
            }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {report.themes}
              </Typography>
            </Paper>
          </Box>
        )}
        
        {/* Strengths & Weaknesses */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="overline" sx={{ 
                color: theme.palette.success.main,
                fontWeight: 600,
                letterSpacing: 2,
                fontSize: '0.7rem',
                mb: 1,
                display: 'block'
              }}>
                STRENGTHS
              </Typography>
              <Paper elevation={0} sx={{ 
                p: 3, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                borderLeft: `4px solid ${theme.palette.success.main}`,
                height: '100%'
              }}>
                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                  {report.strengths || 'No strengths identified'}
                </Typography>
              </Paper>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="overline" sx={{ 
                color: theme.palette.error.main,
                fontWeight: 600,
                letterSpacing: 2,
                fontSize: '0.7rem',
                mb: 1,
                display: 'block'
              }}>
                AREAS FOR IMPROVEMENT
              </Typography>
              <Paper elevation={0} sx={{ 
                p: 3, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                borderLeft: `4px solid ${theme.palette.error.main}`,
                height: '100%'
              }}>
                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                  {report.weaknesses || 'No weaknesses identified'}
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
        
        {/* Market Analysis */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 2,
            fontSize: '0.7rem',
            mb: 1,
            display: 'block'
          }}>
            MARKET ANALYSIS
          </Typography>
          
          {/* Market Analysis Summary */}
          {report.marketAnalysis && report.marketAnalysis !== 'Not specified' && (
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.background.paper, 0.5)
            }}>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                {report.marketAnalysis}
              </Typography>
            </Paper>
          )}
          
          {/* Target Audience */}
          <Paper elevation={0} sx={{ 
            p: 3, 
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.warning.main, 0.02)
          }}>
            <Typography variant="subtitle2" sx={{ 
              fontWeight: 600, 
              mb: 1,
              color: theme.palette.warning.dark,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: '0.7rem'
            }}>
              Target Audience
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
              {report.audienceAppeal || report.targetAudience || 'No target audience analysis provided'}
            </Typography>
          </Paper>
          
          {/* Key Metrics */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center',
                height: '100%'
              }}>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 600
                }}>
                  Marketability
                </Typography>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  mt: 1,
                  color: report.marketability === 'High' ? theme.palette.success.main : 
                         report.marketability === 'Low' ? theme.palette.error.main : 
                         theme.palette.warning.main
                }}>
                  {report.marketability || 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center',
                height: '100%'
              }}>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 600
                }}>
                  Budget Tier
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                  {report.budgetEstimate || 'Not specified'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center',
                height: '100%'
              }}>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 600
                }}>
                  Comparable Avg.
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                  {(() => {
                    const filmsWithBudget = analysisState.comparableFilms.filter(f => Number(f.budget) > 0);
                    if (filmsWithBudget.length === 0) return 'N/A';
                    const total = filmsWithBudget.reduce((sum, f) => sum + (Number(f.budget) || 0), 0);
                    return formatCurrency(total / filmsWithBudget.length);
                  })()}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        {/* Final Evaluation */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 2,
            fontSize: '0.7rem',
            mb: 1,
            display: 'block'
          }}>
            FINAL EVALUATION
          </Typography>
          
          {/* Verdict Banner */}
          <Paper elevation={0} sx={{ 
            p: 4, 
            mb: 3,
            borderRadius: 2,
            textAlign: 'center',
            background: (report.overallEvaluation || '').toLowerCase() === 'recommend' 
              ? 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)'
              : (report.overallEvaluation || '').toLowerCase() === 'pass' 
              ? 'linear-gradient(135deg, #b71c1c 0%, #c62828 100%)'
              : 'linear-gradient(135deg, #e65100 0%, #f57c00 100%)',
            color: 'white'
          }}>
            <Typography variant="overline" sx={{ 
              color: 'rgba(255,255,255,0.7)',
              letterSpacing: 3,
              fontSize: '0.7rem'
            }}>
              RECOMMENDATION
            </Typography>
            <Typography variant="h3" sx={{ 
              fontWeight: 800,
              letterSpacing: 2,
              mt: 1
            }}>
              {(report.overallEvaluation || 'Consider').toUpperCase()}
            </Typography>
          </Paper>
          
          {/* Justification */}
          <Paper elevation={0} sx={{ 
            p: 3, 
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderLeft: `4px solid ${
              (report.overallEvaluation || '').toLowerCase() === 'recommend' ? theme.palette.success.main : 
              (report.overallEvaluation || '').toLowerCase() === 'pass' ? theme.palette.error.main : 
              theme.palette.warning.main
            }`
          }}>
            <Typography variant="subtitle2" sx={{ 
              fontWeight: 600, 
              mb: 2,
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: '0.7rem'
            }}>
              Justification
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
              {report.justification || report.evaluationJustification || 'No detailed justification provided'}
            </Typography>
          </Paper>
          
          {/* Key Considerations */}
          {report.keyConsiderations && Array.isArray(report.keyConsiderations) && report.keyConsiderations.length > 0 && (
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.info.main, 0.02)
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: theme.palette.info.dark,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.7rem'
              }}>
                Key Considerations
              </Typography>
              <Box component="ul" sx={{ 
                pl: 2.5, 
                m: 0,
                '& li': {
                  mb: 1.5,
                  lineHeight: 1.7,
                  '&::marker': {
                    color: theme.palette.info.main
                  }
                }
              }}>
                {report.keyConsiderations.map((consideration, index) => (
                  <li key={index}>
                    <Typography variant="body1" component="span">
                      {consideration}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Paper>
          )}
            
            {/* Craft Analysis Section */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {report.premise && report.premise !== 'Not specified' && (
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.primary.light, 0.05),
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                      Premise
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {report.premise}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {report.structure && report.structure !== 'Not specified' && (
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.secondary.light, 0.05),
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.secondary.main }}>
                      Structure & Pacing
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {report.structure}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {report.dialogue && report.dialogue !== 'Not specified' && (
                <Grid item xs={12} md={4}>
                  <Paper elevation={0} sx={{ 
                    p: 2, 
                    bgcolor: alpha(theme.palette.info.light, 0.05),
                    borderRadius: 2,
                    height: '100%'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.info.main }}>
                      Dialogue
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {report.dialogue}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
            
          {/* Development Notes */}
          {report.developmentNotes && report.developmentNotes !== '' && (
            <Paper elevation={0} sx={{ 
              p: 3, 
              mb: 3,
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.warning.main, 0.3)}`,
              bgcolor: alpha(theme.palette.warning.main, 0.02)
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: theme.palette.warning.dark,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.7rem'
              }}>
                Development Notes
              </Typography>
              <Typography variant="body1" sx={{ 
                lineHeight: 1.7,
                fontStyle: 'italic'
              }}>
                {report.developmentNotes}
              </Typography>
            </Paper>
          )}
            
          {/* Analyst Notes */}
          {report.notes && (
            <Paper elevation={0} sx={{ 
              p: 3, 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.grey[500], 0.02)
            }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 600, 
                mb: 2,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.7rem'
              }}>
                Analyst Notes
              </Typography>
              <Typography variant="body1" sx={{
                lineHeight: 1.7,
                fontStyle: 'italic'
              }}>
                {report.notes}
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    );
  };

  // Render comparable films tab
  const renderComparableFilms = () => {
    if (!analysisState.comparableFilms || analysisState.comparableFilms.length === 0) {
      const isCompleted = analysisState.status === 'completed' || analysisState.status === 'completed_partial';
      return (
        <Box component="div" sx={{ p: 4, textAlign: 'center' }}>
          <Typography component="div" variant="h5" color="textSecondary">
            {isCompleted ? 'No comparable films found' : 'Comparable films data is not available'}
          </Typography>
          <Typography component="div" variant="body1" color="textSecondary" sx={{ mt: 2 }}>
            {isCompleted
              ? 'The analysis completed but did not identify any comparable films for this screenplay.'
              : 'This may be because the analysis is still processing or encountered an error.'}
          </Typography>
        </Box>
      );
    }
    
    // Calculate averages for the summary section
    const validFilms = analysisState.comparableFilms.filter(f => {
      // Try to parse numbers, be more lenient with validation
      const budget = Number(f.budget) || 0;
      const domestic = Number(f.domesticBoxOffice) || 0;
      const international = Number(f.internationalBoxOffice) || 0;
      
      // Consider a film valid if it has at least budget or box office data
      const hasFinancialData = budget > 0 || domestic > 0 || international > 0;
      
      return hasFinancialData;
    });
    
    // Calculate totals and averages with proper number conversion
    const totalBudget = validFilms.reduce((sum, f) => sum + (Number(f.budget) || 0), 0);
    const avgBudget = validFilms.length > 0 
      ? Math.round(totalBudget / validFilms.length)
      : 0;

    const avgDomestic = validFilms.length > 0
      ? Math.round(validFilms.reduce((sum, f) => sum + (Number(f.domesticBoxOffice) || 0), 0) / validFilms.length)
      : 0;
      
    const avgInternational = validFilms.length > 0
      ? Math.round(validFilms.reduce((sum, f) => sum + (Number(f.internationalBoxOffice) || 0), 0) / validFilms.length)
      : 0;
    
    return (
      <Box sx={{ p: isMobile ? 1 : 3 }}>
        {/* Professional Header - Matching Coverage Report Style */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: isMobile ? 3 : 5, 
            mb: 4, 
            borderRadius: 0,
            background: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.primary.main,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            borderBottom: theme.palette.mode === 'dark' ? `4px solid ${theme.palette.primary.main}` : `4px solid ${alpha(theme.palette.common.white, 0.3)}`
          }}
        >
          <Typography variant="overline" sx={{ 
            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : alpha(theme.palette.common.white, 0.8),
            fontWeight: 700,
            letterSpacing: 3,
            fontSize: '0.75rem',
            mb: 1,
            display: 'block'
          }}>
            COMPARABLE FILMS ANALYSIS
          </Typography>
          
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            mt: 2,
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2rem',
            letterSpacing: '-0.02em'
          }}>
            Market Comparables
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: 'rgba(255,255,255,0.7)',
            maxWidth: 700,
            lineHeight: 1.6
          }}>
            {analysisState.comparableFilms.length} films with similar themes, genres, or market positioning analyzed for financial benchmarking.
          </Typography>
          
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 3 }} />
          
          {/* Summary Stats Row */}
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Avg. Budget
                </Typography>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {formatCurrency(avgBudget)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Avg. Domestic
                </Typography>
                <Typography variant="h6" sx={{ color: avgDomestic > avgBudget ? theme.palette.success.main : 'white', fontWeight: 600 }}>
                  {formatCurrency(avgDomestic)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Avg. International
                </Typography>
                <Typography variant="h6" sx={{ color: avgInternational > avgBudget ? theme.palette.success.main : 'white', fontWeight: 600 }}>
                  {formatCurrency(avgInternational)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                  Avg. Worldwide
                </Typography>
                <Typography variant="h6" sx={{ color: (avgDomestic + avgInternational) > avgBudget * 2 ? theme.palette.success.main : 'white', fontWeight: 600 }}>
                  {formatCurrency(avgDomestic + avgInternational)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Key Insights Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 2,
            fontSize: '0.7rem',
            mb: 1,
            display: 'block'
          }}>
            KEY INSIGHTS
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: '100%'
              }}>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 600,
                  display: 'block',
                  mb: 1
                }}>
                  Primary Genre
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {analysisState.comparableFilms[0]?.genre?.split(',')[0] || 'Drama'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: '100%'
              }}>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 600,
                  display: 'block',
                  mb: 1
                }}>
                  Budget Range
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {validFilms.length > 0
                    ? `${formatCurrency(Math.min(...validFilms.map(f => Number(f.budget) || 0)))} - ${formatCurrency(Math.max(...validFilms.map(f => Number(f.budget) || 0)))}`
                    : 'N/A'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ 
                p: 2.5, 
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                height: '100%'
              }}>
                <Typography variant="caption" sx={{ 
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                  fontWeight: 600,
                  display: 'block',
                  mb: 1
                }}>
                  Common Ratings
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {(() => {
                    const ratingCounts = {};
                    analysisState.comparableFilms.forEach(f => {
                      const rating = f.rating || f.mpaaRating || 'Not Rated';
                      if (rating && typeof rating === 'string' && !rating.match(/^\d/)) {
                        ratingCounts[rating] = (ratingCounts[rating] || 0) + 1;
                      }
                    });
                    const sortedRatings = Object.entries(ratingCounts)
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 2)
                      .map(([rating]) => rating);
                    return sortedRatings.length > 0 ? sortedRatings.join(', ') : 'N/A';
                  })()}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
        
        {/* Films Table Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="overline" sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: 2,
            fontSize: '0.7rem',
            mb: 1,
            display: 'block'
          }}>
            FILM DATA
          </Typography>
          
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              borderRadius: 2,
              overflowX: 'auto',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Table sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.primary.main,
                  '& th': {
                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.primary.main,
                    color: 'white',
                    whiteSpace: 'nowrap',
                    borderBottom: theme.palette.mode === 'dark' ? `2px solid ${theme.palette.primary.main}` : `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
                    py: 2
                  }
                }}>
                  <TableCell sx={{ minWidth: 180 }} title="Title of the comparable film">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Title
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }} title="Release year">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Year
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }} title="Distributing studio/company">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Distributor
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }} title="Film genres">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Genre
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }} title="Key cast and crew">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Talent
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} title="Production budget">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Budget
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} title="Domestic box office revenue">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Dom. Box Office
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} title="International box office revenue">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Int'l Box Office
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} title="Worldwide box office revenue">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Worldwide
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} title="Number of screens on opening weekend">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Screens (Open)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }} title="Maximum number of screens during theatrical run">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Screens (Widest)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }} title="MPAA rating (G, PG, PG-13, R, NC-17)">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Rating
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 80 }} title="TMDb audience score (0-10)">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'white' }}>
                      Score
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analysisState.comparableFilms.map((film, index) => {
                  const domestic = film.domesticBoxOffice || 0;
                  const international = film.internationalBoxOffice || 0;
                  const worldwide = domestic + international;
                  const roi = film.budget > 0 ? ((worldwide - film.budget) / film.budget) * 100 : 0;
                  
                  return (
                    <TableRow 
                      key={index} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { 
                          backgroundColor: alpha(theme.palette.action.hover, 0.05) 
                        },
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.info.main, 0.05),
                          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {film.title || 'Unknown'}
                          </Typography>
                          {film.tagline && (
                            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                              "{film.tagline}"
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {film.year || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {film.distributor || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {film.genre ? (
                            film.genre.split(',').map((g, i) => (
                              <Chip 
                                key={i}
                                label={g.trim()} 
                                size="small"
                                sx={{ 
                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.dark,
                                  fontWeight: 500,
                                  fontSize: '0.7rem',
                                  height: 20,
                                  '& .MuiChip-label': { px: 0.75 },
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.info.main, 0.2),
                                  }
                                }}
                              />
                            ))
                          ) : 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const actorNames = extractActorNames(film);
                          const displayNames = actorNames.slice(0, 2).join(', ');
                          const tooltipContent = actorNames.join(', ') || 'No actor data';
                          return (
                            <Tooltip title={tooltipContent} arrow>
                              <Typography variant="body2" sx={{ cursor: actorNames.length ? 'help' : 'default' }}>
                                {actorNames.length ? displayNames || actorNames[0] : 'N/A'}
                                {actorNames.length > 2 && ' + more'}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Budget: ${formatCurrency(film.budget)}`} arrow>
                          <Typography variant="body2">
                            {film.budget ? formatCurrency(film.budget) : 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${film.title} made ${formatCurrency(domestic)} domestically`} arrow>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: domestic > 0 ? 'medium' : 'normal',
                              color: domestic > (film.budget || 0) ? theme.palette.success.main : 'inherit',
                              cursor: domestic > 0 ? 'help' : 'default'
                            }}
                          >
                            {domestic > 0 ? formatCurrency(domestic) : 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${film.title} made ${formatCurrency(international)} internationally`} arrow>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: international > 0 ? 'medium' : 'normal',
                              color: international > (film.budget || 0) ? theme.palette.success.main : 'inherit',
                              cursor: international > 0 ? 'help' : 'default'
                            }}
                          >
                            {international > 0 ? formatCurrency(international) : 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip 
                          title={
                            <Box>
                              <div>Domestic: {formatCurrency(domestic)}</div>
                              <div>International: {formatCurrency(international)}</div>
                              <div>Total: {formatCurrency(worldwide)}</div>
                              {film.budget > 0 && (
                                <div>
                                  ROI: {roi > 0 ? '+' : ''}{roi.toFixed(1)}%
                                </div>
                              )}
                            </Box>
                          } 
                          arrow
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold',
                              color: worldwide > (film.budget || 0) * 2 ? theme.palette.success.main : 'inherit',
                              cursor: 'help'
                            }}
                          >
                            {worldwide > 0 ? formatCurrency(worldwide) : 'N/A'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {film.screensOpening ? film.screensOpening.toLocaleString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {film.screensWidest ? film.screensWidest.toLocaleString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {/* MPAA Rating - combined into single column */}
                        {(film.rating || film.mpaaRating) && (film.rating !== 'Not Rated' || film.mpaaRating !== 'Not Rated') ? (
                          <Chip 
                            label={film.rating || film.mpaaRating} 
                            size="small"
                            sx={{
                              bgcolor: (film.rating === 'R' || film.mpaaRating === 'R') ? '#d32f2f' : 
                                     (film.rating === 'PG-13' || film.mpaaRating === 'PG-13') ? '#f57c00' : 
                                     (film.rating === 'PG' || film.mpaaRating === 'PG') ? '#1976d2' :
                                     (film.rating === 'G' || film.mpaaRating === 'G') ? '#388e3c' : '#616161',
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* TMDb Audience Score */}
                        {film.audienceScore && film.audienceScore > 0 ? (
                          <Tooltip title={`TMDb rating based on ${film.voteCount || 'N/A'} votes`} arrow>
                            <Chip 
                              label={`${film.audienceScore.toFixed(1)}/10`}
                              size="small"
                              sx={{
                                bgcolor: film.audienceScore >= 7 ? '#388e3c' : 
                                       film.audienceScore >= 5 ? '#f57c00' : '#d32f2f',
                                color: 'white',
                                fontWeight: 600,
                                cursor: 'help'
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">N/A</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Averages Row */}
                {validFilms.length > 0 && (
                  <TableRow 
                    sx={{ 
                      backgroundColor: alpha(theme.palette.info.main, 0.05),
                      '& td': { 
                        borderTop: `2px solid ${theme.palette.divider}`,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                        fontWeight: 'bold',
                        py: 1.5
                      }
                    }}
                  >
                    <TableCell colSpan={5} align="right">
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        AVERAGES ({validFilms.length} films):
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Average budget across all comparable films" arrow>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(avgBudget)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Average domestic box office" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: avgDomestic > avgBudget ? theme.palette.success.main : 'inherit'
                          }}
                        >
                          {formatCurrency(avgDomestic)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Average international box office" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: avgInternational > avgBudget ? theme.palette.success.main : 'inherit'
                          }}
                        >
                          {formatCurrency(avgInternational)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Average worldwide box office" arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: (avgDomestic + avgInternational) > avgBudget * 2 ? theme.palette.success.main : 'inherit'
                          }}
                        >
                          {formatCurrency(avgDomestic + avgInternational)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell colSpan={3}>
                      <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                        Based on {validFilms.length} films with complete data
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
        </Box>
          
        {/* Synopsis and Notes Section */}
        {analysisState.comparableFilms.some(film => film.synopsis || (film.miscNotes && film.miscNotes.awards)) && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ 
              color: theme.palette.text.secondary,
              fontWeight: 600,
              letterSpacing: 2,
              fontSize: '0.7rem',
              mb: 1,
              display: 'block'
            }}>
              FILM DETAILS
            </Typography>
              <Grid container spacing={3}>
                {analysisState.comparableFilms.map((film, index) => (
                  ((film.synopsis || (film.miscNotes && film.miscNotes.awards)) && (
                    <Grid item xs={12} key={`details-${index}`}>
                      <Paper 
                        elevation={0} 
                        sx={{ 
                          p: 2, 
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, 
                          borderRadius: 1,
                          '&:hover': {
                            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.1)}`,
                            borderColor: alpha(theme.palette.info.main, 0.3)
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: theme.palette.info.dark }}>
                          {film.title} {film.year ? `(${film.year})` : ''}
                        </Typography>
                        
                        {film.synopsis && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Synopsis:
                            </Typography>
                            <Typography variant="body2" sx={{ pl: 1.5, borderLeft: `2px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                              {film.synopsis}
                            </Typography>
                          </Box>
                        )}
                        
                        {film.miscNotes && film.miscNotes.awards && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                              Awards & Notes:
                            </Typography>
                            <Typography variant="body2" sx={{ pl: 1.5, borderLeft: `2px solid ${alpha(theme.palette.warning.main, 0.3)}` }}>
                              {film.miscNotes.awards}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))
                ))}
              </Grid>
            </Box>
          )}
          
        <Paper elevation={0} sx={{ 
          p: 2, 
          bgcolor: alpha(theme.palette.background.default, 0.5), 
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          mt: 2 
        }}>
          <Typography variant="body2" color="textSecondary">
            <strong>Note:</strong> Budget and box office figures are approximate and may vary from different sources.
            Ratings are based on average critic and audience scores.
          </Typography>
        </Paper>
      </Box>
    );
  };

  if (analysisState && analysisState.status === 'error') {
    return (
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h5" gutterBottom>Analysis Error</Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            <strong>Error:</strong> {analysisState.errorMessage || 'An unknown error occurred during analysis.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            The screenplay analysis encountered an error and could not be completed.
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleRetryAnalysis}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        >
          {loading ? 'Retrying...' : 'Retry Analysis'}
        </Button>
        <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary', fontStyle: 'italic' }}>
          If the problem persists, please contact support for assistance.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2, pb: 6 }}>
      {/* Header with back button and status */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={() => onBack ? onBack() : navigate(-1)} 
            aria-label="back"
            sx={{ mr: 1 }}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Screenplay Analysis
            {renderStatusBadge()}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => setPrintDialogOpen(true)}
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            Print
          </Button>
          {(analysisState?.status === 'error' || analysisState?.status === 'completed_partial') && (
            <Button 
              variant="outlined" 
              color="warning"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetryAnalysis}
              disabled={loading}
            >
              {loading ? 'Retrying...' : 'Retry'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Status message alert */}
      {renderStatusMessage()}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading analysis...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Box sx={{ mt: 1 }}>
            <Button 
              size="small" 
              variant="outlined" 
              color="error" 
              onClick={handleRetryAnalysis}
              startIcon={<RefreshIcon />}
            >
              Retry Analysis
            </Button>
          </Box>
        </Alert>
      ) : !analysisState ? (
        <Alert severity="info">No analysis data available.</Alert>
      ) : (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="Analysis tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Film Coverage Report" icon={<ReportIcon />} iconPosition="start" />
              <Tab label="Comparable Films" icon={<MovieIcon />} iconPosition="start" />
            </Tabs>
          </Box>
          
          {activeTab === 0 && renderFilmCoverageReport()}
          {activeTab === 1 && renderComparableFilms()}
        </>
      )}
      
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)}>
        <DialogTitle>Print Screenplay Analysis</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Choose which sections to include in the printable report.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeCoverage}
                onChange={(e) => setIncludeCoverage(e.target.checked)}
              />
            }
            label="Film Coverage Report"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeComparables}
                onChange={(e) => setIncludeComparables(e.target.checked)}
              />
            }
            label="Comparable Films"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            disabled={!includeCoverage && !includeComparables}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          severity={snackbarSeverity} 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScreenplayAnalysisViewer;
